import { useState, useLayoutEffect } from 'react';
import { TextEditorContextActions, useTextEditorContext } from './context';
import { MessageEditorNameTokens } from '@copilot/common/utils/constant';
import { RichTextEditor } from '@copilot/common/components/editors/richTextEditor/richTextEditor';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import styled from 'styled-components';
import { Input } from 'antd';
import { RecommendedMessageView } from '../../editors/recommendedMessage/view';
import { useRecommendedMessageFeedback } from '@copilot/common/hooks/replyRecommendation';
import { isNil } from 'lodash';

/** Props for Text Editor Body */
export interface ITextEditorBodyProps {
    /** Text as a placeholder */
    placeholder?: string;
    /** Text to show when the editor is disabled */
    disabledText?: string;
    /** Automatic sizing of text area */
    autoSize?: boolean | object;
    /** The size of the rich text editor */
    size?: 'small' | 'default' | 'large';
    /** Whether the textBody is bordered */
    bordered?: boolean;
    /** Callback for when textBody is focused */
    onFocus?: () => void;
    /** Callback for when textBody is blurred */
    onBlur?: () => void;
    /** Callback for textbox value changes */
    onChange?: (value: string) => void;
}

/**
 * This component is used to render the body of the Text Editor.
 */
export const TextEditorBody: FCWithChildren<ITextEditorBodyProps> = (props) => {
    const {
        placeholder,
        disabledText,
        autoSize,
        size = 'default',
        bordered = false,
        onChange,
        onFocus,
        onBlur,
        children,
    } = props;
    const {
        disabled: contextDisabled,
        text,
        dispatchUpdate,
        editorRef,
        isRecommendedMessageActive,
        recommendedFeedback,
        threadId,
        onRegenerateRecommendedMessage,
        isRegeneratingRecommendedMessage,
    } = useTextEditorContext();
    const {
        isRichTextEditorEnabled,
        isReplyRecommendationsEnabled: isReplyRecommendationsEnabledFeatureFlag,
    } = useFeatureFlags();
    const { onUpdateFeedback } = useRecommendedMessageFeedback();
    const [isEditorActive, setIsEditorActive] = useState<boolean>(!isRecommendedMessageActive);

    const shouldShowRecommendedState =
        isReplyRecommendationsEnabledFeatureFlag &&
        isRecommendedMessageActive &&
        !isEditorActive &&
        !contextDisabled;

    useLayoutEffect(() => {
        if (isEditorActive) {
            editorRef?.current?.focus();
        }
    }, [isEditorActive]);

    return (
        <>
            {shouldShowRecommendedState ? (
                <RecommendedMessageView
                    message={text}
                    feedback={recommendedFeedback}
                    onFeedbackChange={(feedbackType, feedbackComment) => {
                        if (!isNil(threadId)) {
                            onUpdateFeedback(threadId, feedbackType, feedbackComment);
                        }
                    }}
                    onClick={() => {
                        setIsEditorActive(true);
                    }}
                    onRegenerate={onRegenerateRecommendedMessage}
                    onDelete={() => {
                        dispatchUpdate({
                            type: TextEditorContextActions.setText,
                            payload: { text: '' },
                        });
                        setIsEditorActive(true);
                    }}
                    isRegenerating={isRegeneratingRecommendedMessage}
                />
            ) : isRichTextEditorEnabled ? (
                <RichTextEditor
                    multiline
                    tokens={MessageEditorNameTokens.map((token) => token.value)}
                    value={text}
                    onChange={(value) => {
                        dispatchUpdate({
                            type: TextEditorContextActions.setText,
                            payload: { text: value },
                        });
                        onChange?.(value);
                        setIsEditorActive(true);
                    }}
                    size={size}
                    bordered={bordered}
                    onFocus={onFocus}
                    onBlur={() => {
                        onBlur?.();
                        setIsEditorActive(false);
                    }}
                    disabled={contextDisabled}
                    disabledValue={disabledText}
                    placeholder={placeholder}
                    ref={editorRef}
                />
            ) : (
                <StyledTextArea
                    value={text}
                    placeholder={contextDisabled ? disabledText : placeholder}
                    disabled={contextDisabled}
                    autoSize={autoSize}
                    bordered={bordered}
                    onFocus={onFocus}
                    onBlur={() => {
                        onBlur?.();
                        setIsEditorActive(false);
                    }}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        dispatchUpdate({
                            type: TextEditorContextActions.setText,
                            payload: { text: newValue },
                        });
                        onChange?.(newValue);
                        setIsEditorActive(true);
                    }}
                />
            )}

            {children}
        </>
    );
};

const StyledTextArea = styled(Input.TextArea)`
    margin: 0px;
    padding: 4px 12px;

    ::placeholder {
        color: ${(props) => props.theme['@Gray/7']};
    }
`;
